/**
 * Guide Tour Models
 * Defines interfaces and constants for the Guide Tour feature.
 */

/**
 * Represents a single step in the Guide Tour.
 */
export interface GuideTourStep {
  /** Unique identifier for the step */
  readonly stepNumber: number;
  /** Title displayed in the step info block (e.g., "Step 1: Let's add your users") */
  readonly title: string;
  /** Description text explaining what the user should do */
  readonly description: string;
  /** Path to the illustration SVG/image for this step */
  readonly illustrationSrc: string;
  /** Primary action buttons for this step */
  readonly actions: GuideTourAction[];
}

/**
 * Type of action to perform when button is clicked.
 * - 'navigate': Standard route navigation
 * - 'navigate-with-action': Navigate and trigger an action via query param
 */
type GuideTourActionType = 'navigate' | 'navigate-with-action';

/**
 * Button variant for styling purposes.
 * - 'primary': Filled button with primary color background
 * - 'secondary': Outlined button with border
 */
type GuideTourActionVariant = 'primary' | 'secondary';

/**
 * Represents an action button within a Guide Tour step.
 */
export interface GuideTourAction {
  /** Button label text */
  readonly label: string;
  /** Icon path (optional, for buttons with icons) */
  readonly iconSrc?: string;
  /** Route to navigate to when clicked */
  readonly route: string;
  /** Whether clicking this button should close the modal */
  readonly closeOnClick: boolean;
  /** Type of action (defaults to 'navigate') */
  readonly actionType?: GuideTourActionType;
  /** Query parameters to add to the route (for triggering specific actions) */
  readonly queryParams?: Record<string, string>;
  /** Button variant for styling (defaults to 'primary') */
  readonly variant?: GuideTourActionVariant;
}

/**
 * Configuration for the Guide Tour feature.
 */
export interface GuideTourConfig {
  /** Total number of steps */
  readonly totalSteps: number;
  /** All steps configuration */
  readonly steps: readonly GuideTourStep[];
  /** Main title shown at the top of the modal */
  readonly mainTitle: string;
}

/**
 * State of the Guide Tour for a specific user.
 */
export interface GuideTourState {
  /** Whether the tour has been completed */
  isCompleted: boolean;
  /** Whether the tour has been dismissed */
  isDismissed: boolean;
  /** Current step (1-based) */
  currentStep: number;
  /** Timestamp when the tour was first shown */
  firstShownAt?: string;
  /** Timestamp when the tour was completed */
  completedAt?: string;
}

/**
 * Local storage key for guide tour state.
 */
export const GUIDE_TOUR_STORAGE_KEY = 'sk12_guide_tour_state';

/**
 * Query parameter name for opening guide tour via URL.
 */
export const GUIDE_TOUR_QUERY_PARAM = 'guideTour';

/**
 * Query parameter value to open guide tour.
 */
export const GUIDE_TOUR_QUERY_VALUE = 'open';

/**
 * Query parameter for triggering CSV upload dialog.
 */
export const OPEN_CSV_DIALOG_PARAM = 'openCsvUpload';
